import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { FormattedMessage } from '../../../../../util/reactIntl';
import css from './Modal.module.css';

const close = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082947/socialbnb/containers/ListingPage/ListingPageComponents/Utils/Modal/ModalAsset/close.png';

const Modal = ({
  isOpen,
  onClose,
  children,
  classNameModal,
  classNameButton,
  classNameBtnImage,
  isHostModal,
  isReviewModal,
  showCloseButtonText = true,
}) => {
  // refs for overlay and the scrollable area
  const overlayRef = useRef(null);
  const scrollableRef = useRef(null);

  // Lock/unlock background scroll (unchanged)
  useEffect(() => {
    if (!isOpen) return;

    const body = document.body;
    const docEl = document.documentElement;
    const scrollbarWidth = window.innerWidth - docEl.clientWidth;
    const scrollY = window.scrollY;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflow = '';
      body.style.paddingRight = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Close on ESC (unchanged)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // redirect overlay scrolling to the modal's scrollable content
  useEffect(() => {
    if (!isOpen) return;
    const overlayEl = overlayRef.current;
    const scrollEl = scrollableRef.current;
    if (!overlayEl || !scrollEl) return;

    const handleWheel = e => {
      // Only redirect if the event target is NOT inside the scrollable area
      if (!scrollEl.contains(e.target)) {
        e.preventDefault(); // stop page scroll
        scrollEl.scrollBy({ top: e.deltaY, left: e.deltaX });
      }
    };

    // Touch support
    let lastY = 0;
    let isTouchingOverlay = false;

    const onTouchStart = e => {
      isTouchingOverlay = !scrollEl.contains(e.target);
      if (isTouchingOverlay) lastY = e.touches[0].clientY;
    };

    const onTouchMove = e => {
      if (!isTouchingOverlay) return;
      const currentY = e.touches[0].clientY;
      const deltaY = lastY - currentY; // finger up -> scroll down
      e.preventDefault();
      scrollEl.scrollBy({ top: deltaY });
      lastY = currentY;
    };

    // Listeners MUST be non-passive so preventDefault works
    overlayEl.addEventListener('wheel', handleWheel, { passive: false });
    overlayEl.addEventListener('touchstart', onTouchStart, { passive: false });
    overlayEl.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      overlayEl.removeEventListener('wheel', handleWheel);
      overlayEl.removeEventListener('touchstart', onTouchStart);
      overlayEl.removeEventListener('touchmove', onTouchMove);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalClassName = `${
    isHostModal ? css.modalContentHost : isReviewModal ? css.modalContentReviews : css.modalContent
  } ${css[classNameModal]}`;

  const buttonClassName = `${css.closeButton} ${css[classNameButton]}`;
  const btnImgClassName = `${css.closeButtonImg} ${css[classNameBtnImage]}`;

  return createPortal(
    <div ref={overlayRef} className={css.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={isHostModal ? css.modalHost : css.modal}>
        <div className={modalClassName} onClick={e => e.stopPropagation()}>
          <button className={buttonClassName} onClick={onClose} aria-label="Close">
            {showCloseButtonText && <FormattedMessage id="FieldDateInput.closeDatePicker" />}
            <img className={btnImgClassName} src={close} alt="" />
          </button>
          <div ref={scrollableRef} className={css.scrollable}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  classNameModal: PropTypes.string,
  classNameButton: PropTypes.string,
  classNameBtnImage: PropTypes.string,
  isHostModal: PropTypes.bool,
  isReviewModal: PropTypes.bool,
};

export default Modal;
